import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Controller } from "react-hook-form";
import FormSimple from "@components/formGroup/formSimple";
import { REGEX_EMAIL, REGEX_PHONE, REGEX_PHONE_INVERSE } from "@helpers/_constants";

export const useSectionFirstEmergencyColumns = () => {
  const { t } = useTranslation("pages.employés.infoPers");

  return useMemo(() => [
    { id: "firstContactName", label: t("Nom Contact") },
    { id: "firstEmployeeRelationship", label: t("Relation avec l'employé") },
    { id: "firstContactPhone", label: t("Téléphone du contact") },
    { id: "firstContactEmail", label: t("Courriel du contact") }
  ], [t])
}

export const SectionFirstEmergencyTableBulkHeader = ({ hiddenColumns = {} }) => {
  const { t: mainTranslator } = useTranslation("pages.employés.infoPers");

  return (
    <>
      <th
        key="firstContactName"
        className={`arh-w-[8.75rem] arh-min-w-[8.75rem] arh-max-w-[8.75rem] arh-bg-greyColor arh-px-5 arh-py-[10px] arh-align-bottom arh-text-xs arh-font-semibold arh-text-darkColor ${hiddenColumns.firstContactName ? "arh-hidden" : ""}`}
      >
        {mainTranslator("Nom Contact")}
      </th>
      <th
        key="firstEmployeeRelationship"
        className={`arh-w-[8.75rem] arh-min-w-[8.75rem] arh-max-w-[8.75rem] arh-bg-greyColor arh-px-5 arh-py-[10px] arh-align-bottom arh-text-xs arh-font-semibold arh-text-darkColor ${hiddenColumns.firstEmployeeRelationship ? "arh-hidden" : ""}`}
      >
        {mainTranslator("Relation avec l'employé")}
      </th>
      <th
        key="firstContactPhone"
        className={`arh-w-[8.75rem] arh-min-w-[8.75rem] arh-max-w-[8.75rem] arh-bg-greyColor arh-px-5 arh-py-[10px] arh-align-bottom arh-text-xs arh-font-semibold arh-text-darkColor ${hiddenColumns.firstContactPhone ? "arh-hidden" : ""}`}
      >
        {mainTranslator("Téléphone du contact")}
      </th>
      <th
        key="firstContactEmail"
        className={`arh-w-[8.75rem] arh-min-w-[8.75rem] arh-max-w-[8.75rem] arh-bg-greyColor arh-px-5 arh-py-[10px] arh-align-bottom arh-text-xs arh-font-semibold arh-text-darkColor ${hiddenColumns.firstContactEmail ? "arh-hidden" : ""}`}
      >
        {mainTranslator("Courriel du contact")}
      </th>
    </>
  );
};

export const SectionFirstEmergencyTableBulkCells = ({
  control,
  employeeIndex,
  employee,
  readOnly = false,
  mustWait,
  getValues,
  hiddenColumns = {}
}) => {
  const { t: mainTranslator } = useTranslation("pages.employés.infoPers");

  const makePhoneRule = useCallback(() => {
    return {
      validate: {
        required: (value) => {
          const isSelected = getValues(`employees.${employeeIndex}.selected`);
          if (!isSelected) return true;

          return value === "" || REGEX_PHONE.test(value);
        }
      }
    };
  }, [getValues, employeeIndex]);

  const makeEmailRule = useCallback(() => {
    return {
      validate: {
        required: (value) => {
          const isSelected = getValues(`employees.${employeeIndex}.selected`);
          if (!isSelected) return true;

          return value === "" || REGEX_EMAIL.test(value);
        }
      }
    };
  }, [getValues, employeeIndex]);

  return (
    <>
      <td
        key="firstContactName"
        className={`arh-w-[8.75rem] arh-min-w-[8.75rem] arh-max-w-[8.75rem] arh-p-4 arh-px-5 arh-font-poppins arh-text-xs arh-text-darkGreyColor ${hiddenColumns.firstContactName ? "arh-hidden" : ""}`}
      >
        <Controller
          control={control}
          name={`employees.${employeeIndex}.firstEmergencyContact.contactName`}
          defaultValue={employee.firstEmergencyContact?.contactName || ""}
          render={({ field: { onChange, value } }) => (
            <FormSimple
              placeholder={mainTranslator("Nom Contact")}
              type="text"
              onChange={onChange}
              value={value}
              readOnly={mustWait || readOnly}
              classInput="!arh-py-[5px] !arh-px-2.5 !arh-pr-8 arh-truncate arh-overflow-hidden arh-whitespace-nowrap"
              edit={true}
            />
          )}
        />
      </td>
      <td
        key="firstEmployeeRelationship"
        className={`arh-w-[8.75rem] arh-min-w-[8.75rem] arh-max-w-[8.75rem] arh-p-4 arh-px-5 arh-font-poppins arh-text-xs arh-text-darkGreyColor arh-whitespace-nowrap ${hiddenColumns.firstEmployeeRelationship ? "arh-hidden" : ""}`}
      >
        <Controller
          control={control}
          name={`employees.${employeeIndex}.firstEmergencyContact.employeeRelationship`}
          defaultValue={employee.firstEmergencyContact?.employeeRelationship || ""}
          render={({ field: { onChange, value } }) => (
            <FormSimple
              placeholder={mainTranslator("Relation avec l'employé")}
              type="text"
              onChange={onChange}
              value={value}
              readOnly={mustWait || readOnly}
              classInput="!arh-py-[5px] !arh-px-2.5 !arh-pr-8 arh-truncate arh-overflow-hidden arh-whitespace-nowrap"
              edit={true}
            />
          )}
        />
      </td>
      <td
        key="firstContactPhone"
        className={`arh-w-[8.75rem] arh-min-w-[8.75rem] arh-max-w-[8.75rem] arh-p-4 arh-px-5 arh-font-poppins arh-text-xs arh-text-darkGreyColor arh-whitespace-nowrap ${hiddenColumns.firstContactPhone ? "arh-hidden" : ""}`}
      >
        <Controller
          control={control}
          name={`employees.${employeeIndex}.firstEmergencyContact.contactPhone`}
          rules={makePhoneRule()}
          defaultValue={employee.firstEmergencyContact?.contactPhone || ""}
          render={({ field: { onChange, value }, fieldState: { invalid } }) => (
            <FormSimple
              placeholder={mainTranslator("Téléphone du contact")}
              type="text"
              onChange={(e) => {
                const cleanedValue = e.target.value.replace(REGEX_PHONE_INVERSE, "");
                onChange(cleanedValue);
              }}
              value={value}
              readOnly={mustWait || readOnly}
              classInput="!arh-py-[5px] !arh-px-2.5 !arh-pr-8 arh-truncate arh-overflow-hidden arh-whitespace-nowrap"
              edit={true}
              invalid={invalid}
            />
          )}
        />
      </td>
      <td
        key="firstContactEmail"
        className={`arh-w-[8.75rem] arh-min-w-[8.75rem] arh-max-w-[8.75rem] arh-p-4 arh-px-5 arh-font-poppins arh-text-xs arh-text-darkGreyColor arh-whitespace-nowrap ${hiddenColumns.firstContactEmail ? "arh-hidden" : ""}`}
      >
        <Controller
          control={control}
          name={`employees.${employeeIndex}.firstEmergencyContact.contactEmail`}
          rules={makeEmailRule()}
          defaultValue={employee.firstEmergencyContact?.contactEmail || ""}
          render={({ field: { onChange, value }, fieldState: { invalid } }) => (
            <FormSimple
              placeholder={mainTranslator("Courriel du contact")}
              type="text"
              onChange={onChange}
              value={value}
              readOnly={mustWait || readOnly}
              classInput="!arh-py-[5px] !arh-px-2.5 !arh-pr-8 arh-truncate arh-overflow-hidden arh-whitespace-nowrap"
              edit={true}
              invalid={invalid}
            />
          )}
        />
      </td>
    </>
  );
};

export const getFirstEmergencyColumnCount = () => 4;